import type { NextApiRequest } from "next";
import { generateCorrelationId } from "@/lib/http/requestContext";

const getObject = (value: unknown): Record<string, unknown> | null => {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : null;
};

export function sanitizeForLog(input: string): string {
  return input.replace(/[\r\n\t]/g, "_");
}

export function generateSecureRandomPart(): string {
  const array = new Uint8Array(8);
  crypto.getRandomValues(array);

  return Array.from(array, (byte) =>
    byte.toString(36).toUpperCase()
  )
    .join("")
    .substring(0, 8);
}

export const normalizeMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;

  if (typeof error === "string") return error;

  const obj = getObject(error);

  if (obj && typeof obj.message === "string") {
    return obj.message;
  }

  try {
    return JSON.stringify(error);
  } catch {
    return "Unknown error";
  }
};

export const normalizeStatus = (
  value: unknown
): string | number | undefined => {
  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  const obj = getObject(value);

  if (obj && (typeof obj.status === "string" || typeof obj.status === "number")) {
    return obj.status;
  }

  return undefined;
};

export const normalizeCode = (err: unknown): string | number => {
  const obj = getObject(err);

  if (obj && (typeof obj.code === "string" || typeof obj.code === "number")) {
    return obj.code;
  }

  return "UNKNOWN";
};

export const getGqlSignatures = (): Record<string, unknown> => {
  const gqlSignaturesRaw = process.env.GQL_SIGNATURES;
  return gqlSignaturesRaw ? JSON.parse(gqlSignaturesRaw) : {};
};

/**
 * Extracts correlation ID from request headers or generates a new one
 * @param req - The Next.js API request
 * @returns The correlation ID as a string
 */
export const getCorrelationId = (req: NextApiRequest): string => {
  const header = req.headers["x-correlation-id"];
  if (typeof header === "string") return header;
  if (Array.isArray(header) && header.length > 0) return header[0];
  return generateCorrelationId();
};